import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay, qtyDisplay } from "@icraftx/shared/v1";
import { Input } from "@/components/ui/input";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import type { DeskTableFeatures } from "@/modules/shell/data-table";

export type PoDetailLine = {
  id: string;
  productId: string;
  productName: string;
  qtyOrdered: string;
  qtyReceived: string;
  qtyOpen: string;
  unitCost: string;
  lineTotal: string;
  lineNet?: string;
  lineTax?: string;
  lineGross?: string;
  trackLot?: boolean;
  trackSerial?: boolean;
};

export type PoDetailReceipt = {
  id: string;
  number: string;
  lifecycle: string;
  supplierInvoiceRef: string | null;
  postedAt: string | null;
};

export type PoDetailPayment = {
  id: string;
  number: string;
  direction: string;
  lifecycle: string;
  amount: string;
  postedAt: string | null;
};

const lineHelper = createColumnHelper<DeskTableFeatures, PoDetailLine>();
const receiptHelper = createColumnHelper<DeskTableFeatures, PoDetailReceipt>();
const payHelper = createColumnHelper<DeskTableFeatures, PoDetailPayment>();

export function poLineColumns(opts: {
  canReceive: boolean;
  recvQty: Record<string, string>;
  setRecvQty: (next: Record<string, string>) => void;
  recvLot: Record<string, string>;
  setRecvLot: (next: Record<string, string>) => void;
  recvSerial: Record<string, string>;
  setRecvSerial: (next: Record<string, string>) => void;
}) {
  const cols = [
    lineHelper.accessor("productName", {
      id: "productName",
      header: "Product",
      size: 200,
    }),
    lineHelper.accessor("qtyOrdered", {
      id: "qtyOrdered",
      header: "Ordered",
      size: 88,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("unitCost", {
      id: "unitCost",
      header: "Cost",
      size: 96,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("qtyReceived", {
      id: "qtyReceived",
      header: "Received",
      size: 96,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("qtyOpen", {
      id: "qtyOpen",
      header: "Open",
      size: 80,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.display({
      id: "lineGross",
      header: "Gross",
      size: 100,
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(row.original.lineGross ?? row.original.lineTotal)}
        </span>
      ),
    }),
  ];

  if (!opts.canReceive) return lineHelper.columns(cols);

  return lineHelper.columns([
    ...cols,
    lineHelper.display({
      id: "receiveQty",
      header: "Receive",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="w-28 font-mono"
            value={opts.recvQty[l.id] ?? ""}
            onChange={(e) =>
              opts.setRecvQty({ ...opts.recvQty, [l.id]: e.target.value })
            }
          />
        );
      },
    }),
    lineHelper.display({
      id: "lot",
      header: "Lot",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (!l.trackLot || Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="min-w-0 w-full font-mono"
            value={opts.recvLot[l.id] ?? ""}
            onChange={(e) =>
              opts.setRecvLot({ ...opts.recvLot, [l.id]: e.target.value })
            }
            placeholder="Required"
          />
        );
      },
    }),
    lineHelper.display({
      id: "serial",
      header: "Serial",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (!l.trackSerial || Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="min-w-0 w-full font-mono"
            value={opts.recvSerial[l.id] ?? ""}
            onChange={(e) =>
              opts.setRecvSerial({
                ...opts.recvSerial,
                [l.id]: e.target.value,
              })
            }
            placeholder="Qty 1"
          />
        );
      },
    }),
  ]);
}

export function poReceiptColumns(companySlug: string) {
  return receiptHelper.columns([
    receiptHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/purchasing/receipts/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    receiptHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/purchasing/receipts/${info.row.original.id}`}
        />
      ),
    }),
    receiptHelper.accessor("supplierInvoiceRef", {
      id: "supplierInvoiceRef",
      header: "Supplier inv",
      size: 140,
      cell: (info) => info.getValue() ?? "—",
    }),
    receiptHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export function poPaymentColumns(companySlug: string) {
  return payHelper.columns([
    payHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/purchasing/payments/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    payHelper.accessor("direction", {
      id: "direction",
      header: "Direction",
      size: 100,
      cell: (info) =>
        info.getValue() === "from_supplier" ? "refund" : "pay",
    }),
    payHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/purchasing/payments/${info.row.original.id}`}
        />
      ),
    }),
    payHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    payHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export type GoodsReceiptLineRow = {
  id: string;
  productId: string;
  productName: string;
  purchaseOrderLineId: string;
  qty: string;
  unitCost: string;
  lotCode: string | null;
  serialCode: string | null;
};

const grLineHelper =
  createColumnHelper<DeskTableFeatures, GoodsReceiptLineRow>();

export function goodsReceiptLineColumns() {
  return grLineHelper.columns([
    grLineHelper.accessor("productName", {
      id: "productName",
      header: "Product",
      size: 200,
    }),
    grLineHelper.accessor("qty", {
      id: "qty",
      header: "Qty",
      size: 88,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    grLineHelper.accessor("unitCost", {
      id: "unitCost",
      header: "Cost",
      size: 96,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    grLineHelper.accessor("lotCode", {
      id: "lotCode",
      header: "Lot",
      size: 120,
      cell: (info) => info.getValue() ?? "—",
    }),
    grLineHelper.accessor("serialCode", {
      id: "serialCode",
      header: "Serial",
      size: 120,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export type SupplierPaymentAllocationRow = {
  id: string;
  purchaseOrderId: string;
  poNumber: string;
  amount: string;
};

const allocHelper =
  createColumnHelper<DeskTableFeatures, SupplierPaymentAllocationRow>();

export function supplierPaymentAllocationColumns(companySlug: string) {
  return allocHelper.columns([
    allocHelper.accessor("poNumber", {
      id: "poNumber",
      header: "Purchase order",
      size: 160,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/purchasing/orders/${info.row.original.purchaseOrderId}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    allocHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
  ]);
}
